import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import {
  findRouteKeyBySlug,
  isSupportedLang,
  ROUTE_SLUGS,
  type RouteKey,
  type SupportedLang,
} from "../../config/routes";

const SITE_URL = "https://coval-tech.com";

// Per-route SEO translation namespaces. Routes without an entry fall back to
// the global "seo" block.
const ROUTE_SEO_NS: Partial<Record<RouteKey, string>> = {
  audit: "audit.seo",
};

// Note: Organization/WebSite JSON-LD lives statically in index.html so it is
// crawlable without JavaScript. This component only manages per-language and
// per-route tags.
const SeoHead = () => {
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  const resolved = i18n.resolvedLanguage ?? "fr";
  const lang: SupportedLang = isSupportedLang(resolved) ? resolved : "fr";

  // pathname looks like "/fr/sites-vitrines" -> ["fr", "sites-vitrines"].
  const slug = pathname.split("/").filter(Boolean)[1] ?? "";
  const routeKey = slug ? findRouteKeyBySlug(slug) : null;

  const pathForLang = (target: SupportedLang): string =>
    routeKey ? `/${target}/${ROUTE_SLUGS[routeKey][target]}` : `/${target}/`;

  const ns = routeKey ? ROUTE_SEO_NS[routeKey] : undefined;
  const title = ns
    ? t(`${ns}.title`, { defaultValue: t("seo.title") })
    : t("seo.title");
  const description = ns
    ? t(`${ns}.description`, { defaultValue: t("seo.description") })
    : t("seo.description");

  const canonicalUrl = `${SITE_URL}${pathForLang(lang)}`;

  return (
    <Helmet>
      <html lang={lang} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />
      <link
        rel="alternate"
        hrefLang="fr"
        href={`${SITE_URL}${pathForLang("fr")}`}
      />
      <link
        rel="alternate"
        hrefLang="en"
        href={`${SITE_URL}${pathForLang("en")}`}
      />
      <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}/`} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:locale" content={lang === "fr" ? "fr_BE" : "en_US"} />
      <meta
        property="og:locale:alternate"
        content={lang === "fr" ? "en_US" : "fr_BE"}
      />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
    </Helmet>
  );
};

export default SeoHead;
